import * as Sentry from "@sentry/react";
import { logEvent } from "./services/loggingService";
import {
  CHUNK_RELOAD_SESSION_KEY,
  DOM_MUTATION_RELOAD_SESSION_KEY,
  INDEXEDDB_RELOAD_SESSION_KEY,
  attemptIndexedDbRecovery,
  getDomMutationKind,
  getErrorMessage,
  isChunkLoadFailureMessage,
  isChunkMimeTypeMismatchMessage,
  isDomMutationMessage,
  isIndexedDbDisconnectMessage,
  isSafariEmptyRangesMessage,
} from "./utils/recoveryErrors";
import { installDomMutationGuard } from "./utils/domMutationGuard";
import {
  FB_IN_APP_BROWSER_BRIDGE_URL_PATTERN,
  WEBKIT_MESSAGE_HANDLERS_PATTERN,
  isThirdPartyInjectedNoise,
} from "./utils/thirdPartyNoise";
import { dropNoisyPostHogExceptions } from "./utils/posthogExceptionFilter";

const SAFARI_EMPTY_RANGES_RELOAD_SESSION_KEY = "kfa_safari_empty_ranges_reload_once";
const RECOVERY_SESSION_RESET_MS = 15000;
const env = import.meta.env as Record<string, string | undefined>;

function resolveSentryRelease(): string | undefined {
  const release =
    env.VITE_SENTRY_RELEASE?.trim() ||
    __APP_SENTRY_RELEASE__?.trim() ||
    env.VITE_COMMIT_SHA?.trim();
  return release || undefined;
}

function getDisabledSentryIntegrations(): Set<string> {
  const raw = env.VITE_SENTRY_DISABLED_INTEGRATIONS;
  if (!raw) {
    return new Set();
  }
  return new Set(
    raw
      .split(",")
      .map((name) => name.trim())
      .filter(Boolean)
  );
}

type RecoveryReason =
  | "chunk_load_failure"
  | "indexeddb_connection_lost"
  | "safari_emptyranges_reference"
  | "dom_mutation";

function isLikelyMobileSafariUserAgent(): boolean {
  const ua = navigator.userAgent;
  const isSafariEngine = /Safari/i.test(ua) && !/(Chrome|Chromium|CriOS|FxiOS|EdgiOS|OPiOS)/i.test(ua);
  const isAppleMobile = /(iPhone|iPad|iPod)/i.test(ua);
  return isSafariEngine && isAppleMobile;
}

function emitRecoveryTelemetry(eventName: string, reason: RecoveryReason, message: string): void {
  void logEvent(eventName, {
    recovery_reason: reason,
    recovery_message: message.slice(0, 300),
  }).catch(() => undefined);
}

function tryOneTimeRecovery(
  event: Pick<Event, "preventDefault">,
  retrySessionKey: string,
  reason: RecoveryReason,
  message: string
): boolean {
  const hasAlreadyRetried = window.sessionStorage.getItem(retrySessionKey) === "1";
  if (hasAlreadyRetried) {
    emitRecoveryTelemetry("app_recovery_reload_skipped_loop_guard", reason, message);
    return false;
  }

  const recoveryEventName =
    reason === "indexeddb_connection_lost"
      ? "app_auto_recovered_indexeddb"
      : reason === "safari_emptyranges_reference"
        ? "app_auto_recovered_safari_emptyranges"
        : reason === "dom_mutation"
          ? "app_auto_recovered_dom_mutation"
          : "app_auto_recovered_chunk_load";
  emitRecoveryTelemetry(recoveryEventName, reason, message);
  Sentry.addBreadcrumb({
    category: "app.recovery",
    message: `Auto-reloading after ${reason}`,
    level: "warning",
    data: {
      reason,
      message,
    },
  });
  event.preventDefault();

  if (reason === "indexeddb_connection_lost") {
    return attemptIndexedDbRecovery();
  }

  window.sessionStorage.setItem(retrySessionKey, "1");
  window.location.reload();
  return true;
}

function attemptRecoveryForMessage(event: Pick<Event, "preventDefault">, message: string): boolean {
  if (isChunkLoadFailureMessage(message)) {
    return tryOneTimeRecovery(event, CHUNK_RELOAD_SESSION_KEY, "chunk_load_failure", message);
  }
  if (isIndexedDbDisconnectMessage(message)) {
    return tryOneTimeRecovery(
      event,
      INDEXEDDB_RELOAD_SESSION_KEY,
      "indexeddb_connection_lost",
      message
    );
  }
  if (isSafariEmptyRangesMessage(message) && isLikelyMobileSafariUserAgent()) {
    return tryOneTimeRecovery(
      event,
      SAFARI_EMPTY_RANGES_RELOAD_SESSION_KEY,
      "safari_emptyranges_reference",
      message
    );
  }
  if (isDomMutationMessage(message)) {
    return tryOneTimeRecovery(event, DOM_MUTATION_RELOAD_SESSION_KEY, "dom_mutation", message);
  }
  return false;
}

function setupGlobalRecoveryHandlers(): void {
  window.addEventListener("unhandledrejection", (event) => {
    const message = getErrorMessage(event.reason);
    if (attemptRecoveryForMessage(event, message)) {
      return;
    }
  });

  window.addEventListener("error", (event) => {
    const message = event.error ? getErrorMessage(event.error) : event.message || "UnknownError";
    if (attemptRecoveryForMessage(event, message)) {
      return;
    }
  });

  // If the app loaded successfully and stays alive briefly, allow future one-time retries.
  window.setTimeout(() => {
    window.sessionStorage.removeItem(CHUNK_RELOAD_SESSION_KEY);
    window.sessionStorage.removeItem(INDEXEDDB_RELOAD_SESSION_KEY);
    window.sessionStorage.removeItem(SAFARI_EMPTY_RANGES_RELOAD_SESSION_KEY);
    window.sessionStorage.removeItem(DOM_MUTATION_RELOAD_SESSION_KEY);
  }, RECOVERY_SESSION_RESET_MS);
}

function isLikelySafariFromTags(tags: Record<string, string> | undefined): boolean {
  if (!tags) {
    return false;
  }
  const browserTag = `${tags.browser ?? ""} ${tags["browser.name"] ?? ""}`;
  return /safari/i.test(browserTag) && !/chrome|chromium|crios|fxios|edgios|opios/i.test(browserTag);
}

function hasUndefinedStackFrame(
  exceptionValues: Array<{
    stacktrace?: { frames?: Array<{ filename?: string | null }> };
  }>
): boolean {
  return exceptionValues.some((exceptionValue) =>
    (exceptionValue.stacktrace?.frames ?? []).some((frame) => {
      const filename = frame.filename?.trim() ?? "";
      return filename.length === 0 || filename === "undefined";
    })
  );
}

function hasMissingSourceSymbolication(
  errors: Array<{ type?: string; url?: string; symbolicator_type?: string }>
): boolean {
  return errors.some(
    (error) =>
      error.type === "js_no_source" ||
      error.symbolicator_type === "missing_source" ||
      (error.url ?? "") === "undefined"
  );
}

function getSessionStorageFlag(key: string): boolean {
  try {
    return window.sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function getPathnameFromUrl(url?: string): string | null {
  if (!url) {
    return null;
  }
  try {
    return new URL(url).pathname;
  } catch {
    return null;
  }
}

// Absorb removeChild/insertBefore calls that external DOM mutation (page
// translation etc.) has invalidated, before React ever mounts — see
// domMutationGuard.ts. The reporter fires at most once per op per page load.
installDomMutationGuard((op) => {
  Sentry.addBreadcrumb({
    category: "dom.guard",
    message: `Intercepted invalid ${op} (external DOM mutation, e.g. translation)`,
    level: "warning",
  });
  void logEvent("dom_mutation_guard_intercepted", { dom_guard_op: op }).catch(() => undefined);
});

// Install our recovery handlers BEFORE Sentry.init so the unhandledrejection
// listener order is ours-first-Sentry-second. That way, when we reload, Sentry
// still captures the event but beforeSend sees INDEXEDDB_RELOAD_SESSION_KEY=1
// and tags auto_reload_attempted: true accurately.
setupGlobalRecoveryHandlers();

Sentry.init({
  dsn: "https://24672e2cf6ca3919afcead9a70cc4de3@o1410024.ingest.us.sentry.io/4510443582849024",
  release: resolveSentryRelease(),
  environment: env.VITE_DEPLOY_TARGET || "development",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations(defaultIntegrations) {
    const disabledIntegrations = getDisabledSentryIntegrations();
    if (disabledIntegrations.size === 0) {
      return defaultIntegrations;
    }
    return defaultIntegrations.filter(
      (integration) => !disabledIntegrations.has(integration.name)
    );
  },
  ignoreErrors: [
    // Injected third-party/extension script noise, not app code.
    /Identifier 'change_ua' has already been declared/i,
    /Failed to execute 'appendChild' on 'Node': Identifier 'change_ua' has already been declared/i,
    // Facebook in-app browser (FB_IAB) bridge teardown, see thirdPartyNoise.ts.
    /Error invoking postMessage:\s*Java\b/i,
    // FB iOS in-app browser probing window.webkit.messageHandlers, see thirdPartyNoise.ts.
    WEBKIT_MESSAGE_HANDLERS_PATTERN,
    // Firebase Auth losing connectivity mid-request (device network, not app code).
    /auth\/network-request-failed/i,
  ],
  // Everything served from the FB in-app browser's injected-script scheme is
  // Facebook's own code running in Facebook's WebView.
  denyUrls: [FB_IN_APP_BROWSER_BRIDGE_URL_PATTERN],
  beforeSend(event) {
    const exceptionValues =
      (event.exception?.values as Array<{
        value?: string;
        type?: string;
        stacktrace?: {
          frames?: Array<{ filename?: string | null; function?: string | null }>;
        };
      }> | undefined) ?? [];
    const sentryErrors =
      ((event as { errors?: Array<{ type?: string; url?: string; symbolicator_type?: string }> })
        .errors as Array<{ type?: string; url?: string; symbolicator_type?: string }> | undefined) ??
      [];
    const hasChangeUaRedeclaration = exceptionValues.some((exceptionValue) => {
      const value = exceptionValue.value ?? "";
      const type = exceptionValue.type ?? "";
      const combined = `${type}: ${value}`;
      return /change_ua/i.test(combined) && /already been declared/i.test(combined);
    });

    if (hasChangeUaRedeclaration) {
      return null;
    }

    const normalizedMessages = [
      event.message ?? "",
      event.logentry?.message ?? "",
      ...exceptionValues.map((exceptionValue) => {
        const value = exceptionValue.value ?? "";
        const type = exceptionValue.type ?? "";
        return `${type}: ${value}`;
      }),
    ];
    const noiseFrames = exceptionValues.flatMap(
      (exceptionValue) => exceptionValue.stacktrace?.frames ?? []
    );

    // Scripts injected by in-app browsers (FB bridge teardown, iOS inline
    // bridge, Google-app max-call-stack recursion, Chrome iOS inline scripts
    // whose frames sit inside the served document) error with no frame of
    // ours. Drop them before they drown real errors.
    if (isThirdPartyInjectedNoise({ messages: normalizedMessages, frames: noiseFrames })) {
      return null;
    }

    const landingLogentryFormatted =
      (event.logentry as { formatted?: string } | undefined)?.formatted ?? "";
    const hasLandingAbortWarningMessage = [
      event.message ?? "",
      landingLogentryFormatted,
      event.logentry?.message ?? "",
    ].some((entry) => /^Landing video warning:\s*AbortError\b/i.test(entry));
    const isLandingVideoPlayRejection =
      event.tags?.feature === "onboarding" &&
      event.tags?.surface === "landing_video" &&
      event.tags?.kind === "play_rejection";
    if (event.level === "warning" && isLandingVideoPlayRejection && hasLandingAbortWarningMessage) {
      return null;
    }

    const hasIndexedDbDisconnect = normalizedMessages.some((entry) =>
      isIndexedDbDisconnectMessage(entry)
    );
    const hasSafariEmptyRanges = normalizedMessages.some((entry) =>
      isSafariEmptyRangesMessage(entry)
    );
    const hasChunkLoadFailure = normalizedMessages.some((entry) =>
      isChunkLoadFailureMessage(entry)
    );
    const hasChunkMimeTypeMismatch = normalizedMessages.some((entry) =>
      isChunkMimeTypeMismatchMessage(entry)
    );
    const hasDomMutation = normalizedMessages.some((entry) => isDomMutationMessage(entry));

    if (hasIndexedDbDisconnect) {
      const autoReloadAttempted = getSessionStorageFlag(INDEXEDDB_RELOAD_SESSION_KEY);
      event.tags = {
        ...event.tags,
        recovery_error_family: "indexeddb_disconnect",
        auto_reload_attempted: autoReloadAttempted ? "true" : "false",
      };
    } else if (hasSafariEmptyRanges) {
      const autoReloadAttempted = getSessionStorageFlag(SAFARI_EMPTY_RANGES_RELOAD_SESSION_KEY);
      event.tags = {
        ...event.tags,
        recovery_error_family: "safari_emptyranges_reference",
        auto_reload_attempted: autoReloadAttempted ? "true" : "false",
      };
      const isSafari = isLikelySafariFromTags(event.tags as Record<string, string> | undefined);
      const undefinedStackFrame = hasUndefinedStackFrame(exceptionValues);
      const missingSource = hasMissingSourceSymbolication(sentryErrors);
      if (isSafari && undefinedStackFrame && missingSource) {
        return null;
      }
    }

    if (hasChunkLoadFailure && !hasIndexedDbDisconnect && !hasSafariEmptyRanges) {
      const autoReloadAttempted = getSessionStorageFlag(CHUNK_RELOAD_SESSION_KEY);
      const chunkErrorFamily = hasChunkMimeTypeMismatch
        ? "chunk_mime_type_mismatch"
        : "chunk_load_failure";
      event.tags = {
        ...event.tags,
        recovery_error_family: chunkErrorFamily,
        auto_reload_attempted: autoReloadAttempted ? "true" : "false",
      };
      event.extra = {
        ...event.extra,
        chunk_load_diagnostics: {
          chunkErrorFamily,
          requestPathname: getPathnameFromUrl(event.request?.url),
          runtimeLikelyMobileSafari: isLikelyMobileSafariUserAgent(),
        },
      };
    }

    // recovery_error_family is the exclusive recovery-pipeline tag (one family
    // per event, established families take precedence); dom_error_family below
    // is the symptom subfamily and is set whenever the message matches.
    if (hasDomMutation && !hasIndexedDbDisconnect && !hasSafariEmptyRanges && !hasChunkLoadFailure) {
      const autoReloadAttempted = getSessionStorageFlag(DOM_MUTATION_RELOAD_SESSION_KEY);
      event.tags = {
        ...event.tags,
        recovery_error_family: "dom_mutation",
        auto_reload_attempted: autoReloadAttempted ? "true" : "false",
      };
    }

    if (hasDomMutation) {
      const requestUrl = event.request?.url ?? "";
      const routeHint = normalizedMessages.find((entry) => entry.includes("/chats"));
      const surface = routeHint || requestUrl.includes("/chats") ? "chat_routes" : "unknown";
      const domMutationKind = normalizedMessages
        .map((entry) => getDomMutationKind(entry))
        .find((kind) => kind !== null);
      event.tags = {
        ...event.tags,
        // "removechild_notfound" keeps its historical literal so existing
        // Sentry searches stay continuous.
        dom_error_family: domMutationKind ?? "removechild_notfound",
        dom_error_surface: surface,
      };
      event.extra = {
        ...event.extra,
        dom_mutation_diagnostics: {
          hasIndexedDbDisconnect,
          hasSafariEmptyRanges,
        },
      };
    }

    return event;
  },
});

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router";
import "./index.css";
import { router } from "./router";
import "./i18n/config";
import { resolvePendingAuthRedirectResult } from "./services/authenticationService";
import { awaitWithinTimeBudget } from "./utils/timeBudget";
import { initializeFirstVisitTracking } from "./services/loggingService";
import { initializeAnalyticsTransportDebugHooks } from "./services/analyticsRuntimeService";
import { validateAnalyticsEnvironment } from "./utils/validateAnalyticsEnv";

// PostHog Analytics
import posthog from "posthog-js";
import { PostHogProvider } from "@posthog/react";

posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: "2026-01-30",
  // Explicit so exception autocapture never depends on the remote project
  // toggle. Console errors stay off: the codebase has ~150 console.error
  // sites and autocapturing them would flood the Error Tracking quota.
  capture_exceptions: {
    capture_unhandled_errors: true,
    capture_unhandled_rejections: true,
    capture_console_errors: false,
  },
  before_send: dropNoisyPostHogExceptions,
});

const isSafeForWork = import.meta.env.VITE_SAFE_FOR_WORK === 'true';

if (isSafeForWork) {
  document.documentElement.classList.add('safe-for-work');
}

// Fix for iOS Safari viewport height
// Sets --app-height CSS variable to the actual inner height
function setAppHeight() {
  const doc = document.documentElement;
  doc.style.setProperty('--app-height', `${window.innerHeight}px`);
}
window.addEventListener('resize', setAppHeight);
window.addEventListener('orientationchange', setAppHeight);
setAppHeight();

/**
 * How long the first paint may wait for Firebase to resolve a pending redirect
 * sign-in before we mount anyway. `getRedirectResult` first awaits the SDK's
 * auth initialization, which reloads a persisted user over the network with no
 * timeout of its own; in an in-app browser (Instagram, Facebook) that request
 * can hang instead of failing, and every millisecond of it used to be a blank
 * page under the host app's spinner.
 */
const AUTH_REDIRECT_RESOLUTION_BUDGET_MS = 2500;

/**
 * Best-effort pre-mount work. Nothing in here may keep the app from rendering:
 * a stalled or throwing side effect costs us telemetry, not the whole page.
 */
async function runPreMountSideEffects(): Promise<void> {
  try {
    validateAnalyticsEnvironment();
    initializeAnalyticsTransportDebugHooks();
    initializeFirstVisitTracking();
  } catch (error) {
    console.warn("Pre-mount analytics initialization failed:", error);
  }

  // Resolve a pending Firebase redirect auth result before mounting the React
  // tree, so the app doesn't settle on an anonymous user first. Bounded: when
  // the budget runs out we mount and let AuthProvider's onAuthStateChanged
  // deliver the real user whenever it arrives.
  const outcome = await awaitWithinTimeBudget(
    resolvePendingAuthRedirectResult(),
    AUTH_REDIRECT_RESOLUTION_BUDGET_MS
  );

  if (outcome.status === "failed") {
    console.warn("Pending auth redirect resolution failed:", outcome.error);
    return;
  }

  if (outcome.status === "timed_out") {
    Sentry.addBreadcrumb({
      category: "app.bootstrap",
      message: "Mounted before the pending auth redirect result resolved",
      level: "warning",
      data: { budget_ms: AUTH_REDIRECT_RESOLUTION_BUDGET_MS },
    });
    // Named for what it measures: no redirect sign-in exists in this app
    // (signInWithRedirect is never called), so this only ever times out on
    // Firebase auth *initialization* — i.e. a slow cold load, not a failed
    // sign-in. Renamed from auth_redirect_resolution_timed_out on 2026-08-31.
    void logEvent("auth_init_resolution_timed_out", {
      budget_ms: AUTH_REDIRECT_RESOLUTION_BUDGET_MS,
    }).catch(() => undefined);
  }
}

async function bootstrapApp() {
  await runPreMountSideEffects();

  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <PostHogProvider client={posthog}>
        <RouterProvider router={router} />
      </PostHogProvider>
    </StrictMode>
  );
}

void bootstrapApp();
